/**
 * Brazil geo data (W8-GEO-REAL, D436, ADR-0068; owner answer D435 §5.0 items 1-2) — country-pack data for the neutral
 * geo cascade, the postal-code port and the pilot service areas.
 *
 *  - **The geo provider cascade** (`geo.providerCascade`, COUNTRY scope `BR`, version 1): the MotoXpress order — HERE →
 *    Google → Nominatim for addresses (stop at an interpolated-or-better match), HERE → Google → OSRM for routes (the
 *    line completed by the self-hosted OSRM), OSRM → HERE for the matrix (25 points / 100 elements) — answering in
 *    `pt-BR`, filtered to Brazil, biased to Brazil's box; a motorcycle profile for `moto`; the donor's straight-line ETA
 *    rule `max(8 min, 3 min/km + 5 min)` and plausibility guard `route ≤ 3 × straight line + 5 km, ≤ 150 km, ≤ 300 min`.
 *    Published by `@vello/pack-seed` through the kernel's GOVERNED seed path (maker ≠ approver); later changes go through
 *    the settings console under four eyes. Until the owner stores the HERE/Google keys and the OSRM URL in the vault
 *    the cascade has no usable vendor, and routes fall back to the straight line with `ROUTE_STRAIGHT_LINE_NO_PROVIDER`.
 *  - **The shared places base** (`geo.places`, COUNTRY scope `BR`, version 1; D669, doc 27): the owner's first policy - NO retention limit for any source
 *    (a stored place is reused until an operator removes it), 15 m to call two answers one place, 25 m to answer a reverse lookup from the base, three
 *    stored places are enough to skip the external provider, eight suggestions at most. Published through the same governed seed path; a per-source
 *    retention (the owner's note on Google's storage terms) is a later version under four eyes, never an edit of this file on a running market.
 *  - **The postal-code port**: `CEP` → address via ViaCEP, then BrasilAPI (the donor `packages/geo/src/cep.ts`), behind
 *    the neutral `@vello/geo` `PostalCodeLookup`. The HTTP client is injected (no `fetch` import here — the pack stays
 *    browser/Hermes-safe); the api binds it.
 *  - **The pilot service areas**: fence set version 2 of Paracatu and Patos de Minas, each version 1 (no blocking area,
 *    D395) plus ONE `operation` fence — the market's service area. The owner has not drawn the areas yet, so each is the
 *    circle the rider market resolver used to hard-code (50 km around the market centre), now as versioned, four-eyes
 *    data; a real polygon replaces it as a new, higher set version.
 */
import { err, ok } from '@vello/core';
import { geoFailure, normalizePostalCode, POSTAL_COMPONENT, } from '@vello/geo';
import { placesPolicySetting } from '@vello/config';
import { BRAZIL } from './brazil.js';
// ---- the governed cascade ----------------------------------------------------------------------------------------------
/** When Brazil's cascade takes effect (the D436 date, UTC). */
export const BRAZIL_GEO_CASCADE_EFFECTIVE_FROM = '2026-09-25T00:00:00Z';
/** Brazil's cascade v1 (the donor's order and constants, as data). */
export const BRAZIL_GEO_CASCADE = {
    geocoding: { providers: ['here', 'google', 'nominatim'], minPrecision: 'interpolated' },
    routing: { providers: ['here', 'google', 'osrm'], completeGeometry: true },
    matrix: { providers: ['osrm', 'here'], maxPoints: 25, maxElements: 100 },
    language: 'pt-BR',
    region: { alpha2: 'BR', alpha3: 'BRA' },
    // Brazil's box in microdegrees (a governed value holds safe integers only).
    boundsMicrodegrees: { south: -33_750_000, west: -73_990_000, north: 5_270_000, east: -28_840_000 },
    profiles: { default: 'car', byCategory: { comfort: 'car', go: 'car', moto: 'motorcycle', standard: 'car' } },
    fallback: { paceMillisecondsPerMeter: 180, fixedSeconds: 300, minimumSeconds: 480 },
    plausibility: { maxDetourBasisPoints: 30_000, marginMeters: 5_000, maxDistanceMeters: 150_000, maxDurationSeconds: 18_000 },
    cacheTtlSeconds: 7_776_000,
    geocodeUserLimit: { capacity: 30, refillPerMinute: 20 },
    timeoutMs: 4_000,
};
/** D847: the BR v3 proposal's per-user allowance of one actor (burst, refill per minute, daily cap per kind). */
const BR_LIMITS = (route, geocode, places) => ({
    route: { burst: route[0], refillPerMinute: route[1], dailyCap: route[2] },
    geocode: { burst: geocode[0], refillPerMinute: geocode[1], dailyCap: geocode[2] },
    places: { burst: places[0], refillPerMinute: places[1], dailyCap: places[2] },
});
/**
 * D847 (geo G1, D845 §4-§7): the PROPOSED Brazil cascade v3 - NOT published by the seed. The orchestrator publishes it through the Admin settings console
 * under four eyes (maker ≠ approver), AFTER the api that reads the D847 members is deployed (an older api refuses the unknown members and the market falls
 * back to the straight line with `GEO_CASCADE_INVALID`). Owner decisions it carries (2026-10-29):
 *  - the PRICE (purpose `quote`) on Google's traffic-aware route - the fastest route, live traffic as the correction - with OSRM as the fallback; every
 *    drawn line, the dispatch and the tracking on OSRM first (free), Google behind it; Google's two-wheeler mode off (premium SKU);
 *  - every cache kept indefinitely (`cacheTtlSeconds: null`; route keys snapped to a 25 m grid; OSRM routes 1 h in Redis; the last good route of a cell
 *    30 days for an outage) - Google's storage terms are a risk the owner accepted (D845 §4.1);
 *  - the DevBase daily caps as the start (rider: route 100, geocode 100, places 250; provider: route 250, geocode 100, places 250; operator - desk, store
 *    and partner staff - generous: route 2 000, geocode 2 000, places 5 000), paid calls only;
 *  - the fallback as the straight line × 1.35 at ~30 km/h + 1 min (a quote never under 2 min; the live ETA has no floor but one minute), and a short
 *    trip (≤ 5 km in a straight line) may add at most 4 km;
 *  - the type-ahead on Google Autocomplete sessions (one billed Details per search), 450 ms debounce; the apps may label "you are at…" from the phone.
 */
export const BRAZIL_GEO_CASCADE_V3_PROPOSAL = {
    // Owner, 2026-10-29: precision first, not the cheapest - Google, HERE and OSRM always present and cascading in this order, the straight line last
    // (HERE is skipped instantly while it has no key).
    geocoding: { providers: ['here', 'google', 'nominatim'], minPrecision: 'interpolated' },
    routing: {
        providers: ['here', 'google', 'osrm'],
        completeGeometry: true,
        byPurpose: {
            quote: { providers: ['google', 'here', 'osrm'], mode: 'fastest', traffic: 'TRAFFIC_AWARE' },
            offer: { providers: ['here', 'google', 'osrm'] },
            pickupLeg: { providers: ['here', 'google', 'osrm'] },
            tripLeg: { providers: ['here', 'google', 'osrm'] },
            deliveryLeg: { providers: ['here', 'google', 'osrm'] },
            default: { providers: ['here', 'google', 'osrm'] },
        },
        mode: 'fastest',
        traffic: 'TRAFFIC_UNAWARE',
        twoWheeler: false,
    },
    matrix: { providers: ['osrm', 'here'], maxPoints: 25, maxElements: 100 },
    language: 'pt-BR',
    region: { alpha2: 'BR', alpha3: 'BRA' },
    boundsMicrodegrees: { south: -33_750_000, west: -73_990_000, north: 5_270_000, east: -28_840_000 },
    // D847 review: a moto Send / partner delivery on the motorcycle graph (the vehicle classes the delivery rate card names).
    // Owner, 2026-10-29: `durationFactorBasisPoints.motorcycle` (a moto filtering between lanes) is a PLACEHOLDER of 10 000 (no change) TO CALIBRATE.
    // D849: it scales every answer that is not a motorcycle time (HERE - its scooter mode keeps car speeds -, Google DRIVE, the OSRM car graph, the
    // straight line), never the motorcycle graph or Google TWO_WHEELER. The moto graph runs at 0.66-0.79 of the car in town (median 0.68,
    // deploy/routing/README.md): a v4 at about 7 000 is the proposed start.
    profiles: { default: 'car', byCategory: { comfort: 'car', go: 'car', moto: 'motorcycle', standard: 'car' }, byVehicleClass: { car: 'car', motorbike: 'motorcycle', van: 'car' }, durationFactorBasisPoints: { motorcycle: 10_000 } },
    fallback: { paceMillisecondsPerMeter: 120, fixedSeconds: 60, minimumSeconds: 120, detourBasisPoints: 13_500, liveFixedSeconds: 0 },
    plausibility: { maxDetourBasisPoints: 30_000, marginMeters: 5_000, maxDistanceMeters: 150_000, maxDurationSeconds: 18_000, shortTrip: { maxStraightMeters: 5_000, maxExtraMeters: 4_000 } },
    cacheTtlSeconds: null,
    // Only Google / HERE answers are stored, reused and kept as the last good route (owner, 2026-10-29); an OSRM answer is always asked again.
    cache: { gridMeters: 25, reuseSeconds: 60, lastGoodTtlSeconds: 2_592_000 },
    geocodeUserLimit: { capacity: 30, refillPerMinute: 20 },
    userLimits: {
        rider: BR_LIMITS([30, 20, 100], [30, 20, 100], [60, 60, 250]),
        provider: BR_LIMITS([60, 30, 250], [30, 20, 100], [60, 60, 250]),
        operator: BR_LIMITS([120, 120, 2_000], [120, 120, 2_000], [240, 240, 5_000]),
    },
    places: { providers: ['google', 'here'], suggestDebounceMs: 450, autocomplete: 'session' },
    breaker: { credentialCooldownSeconds: 300, failureThreshold: 3, cooldownSeconds: 30 },
    nativeReverseGeocode: true,
    // D847 review: a store's band on the road (the self-hosted router only), one decision held 30 min so the cart's band is the submit's.
    delivery: { feeDistanceBasis: 'road', decisionTtlSeconds: 1_800 },
    timeoutMs: 4_000,
};
/**
 * D870 (owner 2026-10-31: real-time traffic OFF everywhere, Google and HERE) - v5 PROPOSAL (not published), the PUBLISHED v4 (the v3 proposal with the motorcycle duration factor 0.70, D849) with
 * `traffic: TRAFFIC_UNAWARE` on EVERY purpose, the quote included, and the SAME vendor order as v4. Google then bills Compute Routes Essentials (10,000 free, 5.00 per 1,000) instead of Pro
 * (5,000 free, 10.00 per 1,000), and HERE - which now honours the switch - sends `departureTime=any` and bills "Routing Car" (30,000 free, 0.88 per 1,000) instead of "Time Aware Routing"
 * (5,000 free, 2.92 per 1,000). The ETA loses live traffic; a route cached before is kept apart (the cache variant carries the traffic model). Publishing is the owner's four-eyes decision.
 */
export const BRAZIL_GEO_CASCADE_V5_PROPOSAL = {
    ...BRAZIL_GEO_CASCADE_V3_PROPOSAL,
    routing: {
        ...BRAZIL_GEO_CASCADE_V3_PROPOSAL.routing,
        byPurpose: {
            quote: { providers: ['google', 'here', 'osrm'], mode: 'fastest', traffic: 'TRAFFIC_UNAWARE' },
            offer: { providers: ['here', 'google', 'osrm'], traffic: 'TRAFFIC_UNAWARE' },
            pickupLeg: { providers: ['here', 'google', 'osrm'], traffic: 'TRAFFIC_UNAWARE' },
            tripLeg: { providers: ['here', 'google', 'osrm'], traffic: 'TRAFFIC_UNAWARE' },
            deliveryLeg: { providers: ['here', 'google', 'osrm'], traffic: 'TRAFFIC_UNAWARE' },
            default: { providers: ['here', 'google', 'osrm'], traffic: 'TRAFFIC_UNAWARE' },
        },
        traffic: 'TRAFFIC_UNAWARE',
    },
    profiles: { ...BRAZIL_GEO_CASCADE_V3_PROPOSAL.profiles, durationFactorBasisPoints: { motorcycle: 7_000 } },
};
// ---- the postal-code port (CEP)--------------------------------------------------------------------------------------
/** The format of a Brazilian postal code: 8 digits, shown `#####-###`, labelled CEP. */
export const BRAZIL_POSTAL_CODE_FORMAT = {
    length: 8,
    charset: 'digits',
    mask: '#####-###',
    labels: { pt: 'CEP', en: 'CEP (postal code)', es: 'CEP (código postal)', it: 'CEP (codice postale)' },
};
/**
 * D793: how a Brazilian geocoder writes an address line - "R. Francisco de Paula e Silva, 72 - Cidade Nova, Patos de Minas - MG, 38706-428, Brasil" - so the apps can show
 * the short form (street, number, neighbourhood): the country is written "Brasil" (or "Brazil"), the CEP is `#####-###` as a whole part, the city part ends "- UF" and the
 * neighbourhood follows the house number after " - ".
 */
export const BRAZIL_ADDRESS_DISPLAY_RULE = {
    countryNames: ['Brasil', 'Brazil'],
    postalCodePattern: '^\\d{5}-?\\d{3}$',
    localityPattern: '\\s-\\s[A-Z]{2}$',
    neighbourhoodSeparator: ' - ',
};
const VIACEP_URL = 'https://viacep.com.br/ws';
const BRASILAPI_URL = 'https://brasilapi.com.br/api/cep/v2';
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const text = (v) => (typeof v === 'string' && v.trim() !== '' ? v.trim() : undefined);
function addressOf(code, parts) {
    const components = [
        { kind: POSTAL_COMPONENT.postalCode, value: code },
        ...(parts.thoroughfare !== undefined ? [{ kind: POSTAL_COMPONENT.thoroughfare, value: parts.thoroughfare }] : []),
        ...(parts.sublocality !== undefined ? [{ kind: POSTAL_COMPONENT.sublocality, value: parts.sublocality }] : []),
        ...(parts.locality !== undefined ? [{ kind: POSTAL_COMPONENT.locality, value: parts.locality }] : []),
        ...(parts.area !== undefined ? [{ kind: POSTAL_COMPONENT.administrativeArea, value: parts.area }] : []),
    ];
    const formatted = [parts.thoroughfare, parts.sublocality, parts.locality !== undefined && parts.area !== undefined ? `${parts.locality} - ${parts.area}` : parts.locality ?? parts.area]
        .filter((p) => p !== undefined && p !== '')
        .join(', ');
    return { formatted, components };
}
/**
 * ViaCEP, then BrasilAPI (the donor order). A registry that knows the code but not its street (a city-wide code) still
 * answers — the person then types the street. `NOT_FOUND` only when neither knows the code; `PROVIDER_UNAVAILABLE` when
 * neither could be asked.
 */
export function createBrazilPostalCodeLookup(deps) {
    const timeoutMs = deps.timeoutMs ?? 4_000;
    const call = async (url, options) => {
        try {
            const res = await deps.http({ url, timeoutMs, ...(options?.signal !== undefined ? { signal: options.signal } : {}) });
            let body;
            try {
                body = await res.json();
            }
            catch {
                body = undefined;
            }
            return { status: res.status, body };
        }
        catch {
            return 'unreachable';
        }
    };
    return {
        format: BRAZIL_POSTAL_CODE_FORMAT,
        normalize: (code) => normalizePostalCode(BRAZIL_POSTAL_CODE_FORMAT, code),
        async lookup(query, options) {
            const code = normalizePostalCode(BRAZIL_POSTAL_CODE_FORMAT, query.code);
            if (code === undefined)
                return err(geoFailure('INVALID_REQUEST', 'a postal code has 8 digits'));
            let reached = false;
            const via = await call(`${VIACEP_URL}/${code}/json/`, options);
            if (via !== 'unreachable') {
                reached = true;
                if (via.status >= 200 && via.status < 300 && isObj(via.body) && via.body.erro !== true && via.body.erro !== 'true') {
                    const locality = text(via.body.localidade);
                    if (locality !== undefined) {
                        return ok({
                            postalCode: code,
                            address: addressOf(code, { thoroughfare: text(via.body.logradouro), sublocality: text(via.body.bairro), locality, area: text(via.body.uf) }),
                            provenance: { providerId: 'viacep', cached: false },
                        });
                    }
                }
            }
            const api = await call(`${BRASILAPI_URL}/${code}`, options);
            if (api !== 'unreachable') {
                reached = true;
                if (api.status >= 200 && api.status < 300 && isObj(api.body) && text(api.body.city) !== undefined) {
                    const loc = isObj(api.body.location) && isObj(api.body.location.coordinates) ? api.body.location.coordinates : undefined;
                    const latitude = loc !== undefined ? Number(loc.latitude) : NaN;
                    const longitude = loc !== undefined ? Number(loc.longitude) : NaN;
                    const location = Number.isFinite(latitude) && Number.isFinite(longitude) && Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180 && (latitude !== 0 || longitude !== 0) ? { latitude, longitude } : undefined;
                    return ok({
                        postalCode: code,
                        address: addressOf(code, { thoroughfare: text(api.body.street), sublocality: text(api.body.neighborhood), locality: text(api.body.city), area: text(api.body.state) }),
                        ...(location !== undefined ? { location } : {}),
                        provenance: { providerId: 'brasilapi', cached: false },
                    });
                }
            }
            return err(reached ? geoFailure('NOT_FOUND', 'no registry knows this postal code') : geoFailure('PROVIDER_UNAVAILABLE', 'no postal registry could be reached'));
        },
    };
}
// ---- the pilot service areas (G-03) ----------------------------------------------------------------------------------
/** The pilot markets' service-area circles (centre, radius) until the owner draws the real polygons. */
const PILOT_SERVICE_AREAS = [
    { marketId: 'br:paracatu', center: { latitude: -17.2252, longitude: -46.8711 }, radiusMeters: 50_000 },
    { marketId: 'br:patos-de-minas', center: { latitude: -18.5789, longitude: -46.5181 }, radiusMeters: 50_000 },
];
/**
 * Fence set version 2 of each pilot market: version 1 had no fence (no blocking area); version 2 adds the market's
 * `operation` fence — its service area — and still no blocking area. Published whole, maker ≠ approver, by
 * `@vello/pack-seed` `seedServiceAreas` (after `seedDispatchEligibility` published version 1).
 */
export function brazilPilotServiceAreaSets() {
    return PILOT_SERVICE_AREAS.map((a) => {
        const fence = {
            id: `${a.marketId}:service-area`,
            marketId: a.marketId,
            purpose: 'operation',
            geometry: { kind: 'circle', center: a.center, radiusMeters: a.radiusMeters },
            priority: 0,
            version: 1,
            effectiveFrom: BRAZIL_GEO_CASCADE_EFFECTIVE_FROM,
        };
        return { marketId: a.marketId, version: 2, fences: [fence], reasonCode: 'PILOT_MARKET_SERVICE_AREA' };
    });
}
// ---- the shared places base --------------------------------------------------------------------------------------------
/** When Brazil's places policy takes effect (the day the places program was approved, UTC). */
export const BRAZIL_PLACES_EFFECTIVE_FROM = '2026-10-22T00:00:00Z';
/** Brazil's first `geo.places`: no retention limit for any source (owner, 2026-10-22), and the platform's matching and suggestion defaults. */
export const BRAZIL_PLACES_V1 = {
    retentionDays: null,
    retentionDaysBySource: {},
    dedupMeters: 15,
    reverseMatchMeters: 25,
    suggestMinLocalResults: 3,
    suggestMaxResults: 8,
};
export function brazilPlacesSettings() {
    return [placesPolicySetting({ scopeRef: BRAZIL, value: BRAZIL_PLACES_V1, version: 1, effectiveFrom: BRAZIL_PLACES_EFFECTIVE_FROM })];
}
/** Publish Brazil's places policy through a (governed) policy `publish`. Returns the count accepted. */
export async function publishBrazilPlacesPolicies(publish, newId, now) {
    let count = 0;
    for (const setting of brazilPlacesSettings()) {
        if (await publish(setting, newId(), now))
            count += 1;
    }
    return count;
}
//# sourceMappingURL=geo.js.map